import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { modules, entities } from "byzantine";
import type Account from "byzantine/src/Account";
import styles from "./InvoiceContainer.module.scss";
import PreviewCard from "./PreviewCard";
import { INVOICE_ROUTE, INVOICE_REVIEW_ROUTE } from "./InvoiceRoutes";
import { useInvoiceFlow } from "./InvoiceFlowContext";
import { useSudoContext } from "../../SudoContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import AccountContext from "../../contexts/AccountContext";
import ComboAccountSelector from "../../ComboAccountSelector";
import ACHCompanyMissingAlert from "../ach/ACHCompanyMissingAlert";
import * as formAdapters from "../../../src/adapters";
import InvoiceDeliverySpeedSection from "./InvoiceDeliverySpeedSection";
import InvoicePaymentDetailsSection from "./InvoicePaymentDetailsSection";
import InvoiceAdditionalDetailsSection from "./InvoiceAdditionalDetailsSection";
import useInvoicePrefill from "./useInvoicePrefill";
import useInvoiceHighlight from "./useInvoiceHighlight";

const DetailsStep = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { previewPages, previewUrl, previewMimeType, clearUploadedFile } =
    useInvoiceFlow();
  const { establishSudo } = useSudoContext();
  const institution = useInstitutionSettings();
  const { accounts } = useContext(AccountContext);
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const isInstantDelivery = modules.achPayments.useIsInstantACHDelivery();
  // the internal note and additional details are collected on this step, so
  // validate them here
  const { onContinue } = modules.achPayments.usePaymentDetails({
    shouldValidateInternalNote: true,
    shouldValidateAdditionalDetails: !isInstantDelivery,
  });
  // seed amount/date/memo from what the backend extracted off the invoice
  useInvoicePrefill();

  const { highlight, fieldHandlers, clearHighlight } = useInvoiceHighlight();

  // invoice pay reuses the ACH payment sudo institution setting
  const sudoRequired = Boolean(institution.sudo_mode_required_for_ach_payments);

  const validAccounts = accounts.filter((account: Account) =>
    account.isValidAchTransferSource()
  );

  const reviewRoute = `/transfer/${INVOICE_ROUTE}/${INVOICE_REVIEW_ROUTE}`;

  const { isACHCompanyMissing, loading: isACHCompanyLoading } =
    entities.achCompanies.useACHCompanyForAccount(form.values.fromAccount);
  // instant delivery is sent over FedNow, whose payload carries no ACH company,
  // so those payments are fine from an account that has none
  const requiresACHCompany = !isInstantDelivery;
  const blockForMissingACHCompany = requiresACHCompany && isACHCompanyMissing;
  const isACHCompanyPending = requiresACHCompany && isACHCompanyLoading;
  const fromAccountProps = formAdapters.select(form, "fromAccount");
  const achCompanyFieldError = blockForMissingACHCompany
    ? l10n.getString("ach-company-missing-field-error")
    : undefined;

  const onBack = () => navigate(`/transfer/${INVOICE_ROUTE}`);

  const handleNext = () => {
    // the selected account has no ACH company, so the payment cannot be
    // originated — keep the user on this step to pick another account.
    // while the lookup is still pending we don't yet know, so don't advance
    if (blockForMissingACHCompany || isACHCompanyPending) return;

    const goToReview = () => navigate(reviewRoute);
    const trySudo = () => {
      establishSudo(reviewRoute).catch(() => {
        // sudo cancelled or failed — stay on the details step
      });
    };

    onContinue(sudoRequired ? trySudo : goToReview);
  };

  return (
    <div className={styles.invoiceUploaderContainer}>
      <div className={styles.uploaderColumn}>
        <PreviewCard
          previewUrl={previewUrl}
          previewPages={previewPages}
          previewMimeType={previewMimeType}
          onRemove={clearUploadedFile}
          highlight={highlight}
          showZoomControls
          onZoomInteraction={clearHighlight}
        />
      </div>

      <div className={styles.detailsColumn}>
        <h1 className="fontFamily--heading fontSize--heading1">
          {l10n.getString("invoice-review-title")}
        </h1>
        <p className="fontColor--secondary margin--top--s">
          {l10n.getString("invoice-review-blurb")}
        </p>

        {blockForMissingACHCompany ? (
          <div className="margin--top--l">
            <ACHCompanyMissingAlert isActive />
          </div>
        ) : null}

        <div className="margin--top--xl">
          <h4 className="fontSize--m nds-sans margin--bottom--m">
            {l10n.getString("invoice-details-how-to-pay")}
          </h4>
          <ComboAccountSelector
            label={l10n.getString("single-ach-payment-from-account")}
            accounts={validAccounts}
            isDestination={false}
            showAddExternalAccountLink={false}
            {...fromAccountProps}
            error={achCompanyFieldError || fromAccountProps.error}
            errorText={achCompanyFieldError || fromAccountProps.errorText}
          />
        </div>

        <div className="margin--top--l">
          <InvoiceDeliverySpeedSection />
        </div>

        <div className={styles.paymentDetailsSection}>
          <InvoicePaymentDetailsSection fieldHandlers={fieldHandlers} />
        </div>

        {!isInstantDelivery ? (
          <div className="margin--top--m">
            <InvoiceAdditionalDetailsSection />
          </div>
        ) : null}
      </div>

      <div className={styles.actionsRow}>
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="plain"
              label={l10n.getString("button-back")}
              onClick={onBack}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleNext}
              isLoading={isACHCompanyPending}
            />
          </Row.Item>
        </Row>
      </div>
    </div>
  );
};

export default DetailsStep;
